import { useState, useEffect } from 'react';
import { getOverview } from '../api';
import AdminMembers from './AdminMembers';
import AdminDraws from './AdminDraws';
import AdminMoney from './AdminMoney';
import AdminSettings from './AdminSettings';
import AdminQr from './AdminQr';
import { colours as c, font, gbp, num, shortDate } from '../theme';

/* ---------- shared bits -------------------------------------------------- */

const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div className="skeleton" style={{ width, height, ...style }} />
);

// 40 / 25 / 15 / 20 split bar
export const SplitBar = ({ pct, height = 10 }) => {
  const p = pct || { first: 40, second: 25, third: 15, retained: 20 };
  return (
    <div style={{ display: 'flex', height, borderRadius: 2, overflow: 'hidden', gap: 2 }}>
      <div style={{ width: `${p.first}%`, background: c.teal }} />
      <div style={{ width: `${p.second}%`, background: c.tealSoft }} />
      <div style={{ width: `${p.third}%`, background: c.sageSoft }} />
      <div style={{ width: `${Math.max(0, p.retained)}%`, background: c.sage }} />
    </div>
  );
};

// Simple CSS bar chart — matches the design, no chart library needed here
const MiniBars = ({ series }) => {
  const max = Math.max(1, ...series.map(s => s.income));
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 10, minHeight: 150 }}>
      {series.map((s, i) => (
        <div key={i} style={S.barCol}>
          <div
            style={{
              width: '100%',
              height: `${Math.max(2, (s.income / max) * 100)}%`,
              background: s.isCurrent ? c.teal : '#c9d6c1',
              borderRadius: '2px 2px 0 0',
            }}
            title={gbp(s.income)}
          />
          <span style={{ fontSize: 11, color: s.isCurrent ? c.ink : c.muted, fontWeight: s.isCurrent ? 600 : 400 }}>
            {s.month.split(' ')[0]}
          </span>
        </div>
      ))}
    </div>
  );
};

const NAV = [
  { key: 'overview', label: 'Overview' },
  { key: 'members',  label: 'Members'  },
  { key: 'draws',    label: 'Draws'    },
  { key: 'money',    label: 'Money'    },
  { key: 'settings', label: 'Settings' },
  { key: 'qrcode',   label: 'QR poster'},
];

/* ---------- overview tab ------------------------------------------------- */

function Overview({ data, loading, onGoTo, firstName }) {
  if (loading || !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <Skeleton height="180px" />
        <Skeleton height="90px" />
        <Skeleton height="220px" />
      </div>
    );
  }

  const { pot, nextDraw, nudge, series } = data;
  const drawWhen =
    nextDraw.daysUntil <= 0 ? 'Today, 9:00am'
      : nextDraw.daysUntil === 1 ? 'Tomorrow, 9:00am'
        : `${nextDraw.daysUntil} days away`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

      {/* pot + side column */}
      <div style={S.overviewTop} className="grid-2">

        <div style={S.card}>
          <div style={S.cardHeadRow}>
            <span style={S.eyebrow}>{pot.month}'s pot</span>
            <span style={S.small}>
              {num(pot.memberCount)} member{pot.memberCount === 1 ? '' : 's'} covered × {gbp(pot.monthlyRate)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
            <span style={S.potBig}>{gbp(pot.total)}</span>
            {pot.change !== 0 && (
              <span style={{ ...S.potChange, color: pot.change > 0 ? c.sage : c.lapsedFg }}>
                {pot.change > 0 ? '+' : ''}{gbp(pot.change)} on last month
              </span>
            )}
          </div>

          <SplitBar pct={pot.percents} />

          <div style={S.prizeGrid} className="grid-4">
            <div style={S.prizeCell}>
              <span style={S.prizeLabel}>1st · {pot.percents.first}%</span>
              <span style={S.prizeValue}>{gbp(pot.first)}</span>
            </div>
            <div style={S.prizeCell}>
              <span style={S.prizeLabel}>2nd · {pot.percents.second}%</span>
              <span style={S.prizeValue}>{gbp(pot.second)}</span>
            </div>
            <div style={S.prizeCell}>
              <span style={S.prizeLabel}>3rd · {pot.percents.third}%</span>
              <span style={S.prizeValue}>{gbp(pot.third)}</span>
            </div>
            <div style={S.prizeCell}>
              <span style={{ ...S.prizeLabel, color: c.sage }}>Fields · {pot.percents.retained}%</span>
              <span style={S.prizeValue}>{gbp(pot.retained)}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={S.drawCard}>
            <span style={S.eyebrowOnTeal}>Next draw</span>
            <span style={S.drawWhen}>{drawWhen}</span>
            <span style={S.drawSub}>
              {nextDraw.dateLine} · draws {nextDraw.coversMonth} · runs automatically
            </span>
          </div>

          <div style={{ ...S.card, flex: 1, gap: 14 }}>
            <div style={S.cardHeadRow}>
              <span style={S.eyebrow}>Needs a nudge</span>
              {data.nudgeCount > 0 && <span style={S.countPill}>{num(data.nudgeCount)}</span>}
            </div>

            {data.nudgeCount === 0 ? (
              <span style={S.small}>Everyone's paid up. Nothing to chase.</span>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 14 }}>
                  {nudge.map(n => (
                    <div key={n.id} style={S.nudgeRow}>
                      <span style={{ color: c.ink }}>{n.name}</span>
                      <span style={{ color: n.status === 'lapsed' ? c.lapsedFg : c.muted }}>
                        {n.status === 'lapsed' ? `lapsed ${shortDate(n.lapsedOn)}` : 'never paid'}
                      </span>
                    </div>
                  ))}
                </div>
                {data.nudgeCount > nudge.length && (
                  <button style={S.linkBtn} onClick={() => onGoTo('members')}>
                    See all {num(data.nudgeCount)}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* stat strip */}
      <div style={S.statStrip} className="grid-4">
        <div style={S.statCell}>
          <span style={S.statLabel}>Collected this year</span>
          <span style={S.statValue}>{gbp(data.totalThisYear)}</span>
        </div>
        <div style={S.statCell}>
          <span style={S.statLabel}>Collected all time</span>
          <span style={S.statValue}>{gbp(data.totalAllTime)}</span>
        </div>
        <div style={S.statCell}>
          <span style={S.statLabel}>Active members</span>
          <span style={S.statValue}>
            {num(data.activeMemberCount)}{' '}
            <span style={{ fontSize: 14, color: c.muted }}>of {num(data.memberTotal)}</span>
          </span>
        </div>
        <div style={S.statCell}>
          <span style={S.statLabel}>Annual / Monthly</span>
          <span style={S.statValue}>
            {num(data.activeAnnualCount)} / {num(data.activeMonthlyCount)}
          </span>
        </div>
      </div>

      {/* money in */}
      <div style={{ ...S.card, gap: 16 }}>
        <div style={S.cardHeadRow}>
          <span style={{ fontSize: 15, fontWeight: 600, color: c.ink }}>
            Money in, last 12 months
          </span>
        </div>
        <MiniBars series={series} />
      </div>
    </div>
  );
}

/* ---------- shell -------------------------------------------------------- */

export default function AdminShell({ member, onLogout }) {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    getOverview()
      .then(res => setOverview(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const firstName = (member?.name || '').split(' ')[0];
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  const counts = {
    members: overview?.memberTotal,
    draws: overview?.drawCount,
  };

  const go = (key) => { setTab(key); setNavOpen(false); };

  const heading = tab === 'overview'
    ? `${greeting}${firstName ? `, ${firstName}` : ''}`
    : NAV.find(n => n.key === tab)?.label;

  return (
    <div style={S.shell}>

      {/* sidebar */}
      <aside style={{ ...S.sidebar, ...(navOpen ? S.sidebarOpen : {}) }} className={`admin-sidebar${navOpen ? ' open' : ''}`}>
        <div style={S.brandRow}>
          <div style={S.logoWrap}>
            <img src="/logo-bpfa.jpg" alt="BPFA" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={S.brand}>100 Club</span>
            <span style={S.brandSub}>Committee tools</span>
          </div>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(n => (
            <button
              key={n.key}
              className="admin-nav"
              style={tab === n.key ? S.navOn : S.navOff}
              onClick={() => go(n.key)}
            >
              <span>{n.label}</span>
              {counts[n.key] != null && (
                <span style={{ fontSize: 13, color: tab === n.key ? c.muted : c.onTealDim }}>
                  {num(counts[n.key])}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div style={S.sidebarFoot}>
          <span style={{ fontSize: 14, color: c.onTeal }}>{member?.name}</span>
          <button style={S.signOutLink} onClick={onLogout}>Treasurer · sign out</button>
        </div>
      </aside>

      {/* main */}
      <div style={S.main}>
        <div style={S.topBar} className="pad-header">
          <button style={S.burger} className="show-mobile-only" onClick={() => setNavOpen(!navOpen)}>
            ☰
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
            <h1 style={S.h1}>{heading}</h1>
            <span style={S.small}>
              {new Date().toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
              })}
            </span>
          </div>
        </div>

        <div style={S.content} className="pad-page">
          {tab === 'overview' && (
            <Overview data={overview} loading={loading} onGoTo={go} firstName={firstName} />
          )}
          {tab === 'members' && <AdminMembers />}
          {tab === 'draws' && <AdminDraws />}
          {tab === 'money' && <AdminMoney />}
          {tab === 'settings' && <AdminSettings />}
          {tab === 'qrcode' && <AdminQr />}
        </div>
      </div>
    </div>
  );
}

/* ---------- styles ------------------------------------------------------- */

const S = {
  shell: { minHeight: '100vh', background: c.paper, display: 'flex', fontFamily: font.sans },

  sidebar: {
    width: 252, flex: 'none', background: c.teal, padding: '24px 18px',
    display: 'flex', flexDirection: 'column', gap: 28,
  },
  sidebarOpen: {},
  brandRow: { display: 'flex', alignItems: 'center', gap: 12 },
  logoWrap: {
    width: 42, height: 42, borderRadius: '50%', overflow: 'hidden',
    background: '#fff', flex: 'none',
  },
  brand: { fontFamily: font.serif, fontSize: 19, color: c.onTeal, lineHeight: 1.1 },
  brandSub: {
    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.onTealDim,
  },
  navOff: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '11px 12px', borderRadius: 3, color: c.onTealSub, fontSize: 15,
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontFamily: font.sans, textAlign: 'left', width: '100%',
  },
  navOn: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '11px 12px', borderRadius: 3, background: c.paper, color: c.ink,
    fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer',
    fontFamily: font.sans, textAlign: 'left', width: '100%',
  },
  sidebarFoot: {
    marginTop: 'auto', paddingTop: 18, borderTop: '1px solid #4f6a6a',
    display: 'flex', flexDirection: 'column', gap: 3,
  },
  signOutLink: {
    background: 'none', border: 'none', padding: 0, textAlign: 'left',
    fontSize: 12, color: c.onTealDim, cursor: 'pointer', fontFamily: font.sans,
  },

  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  topBar: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '26px 36px 20px', borderBottom: `1px solid ${c.hairline}`,
  },
  burger: {
    display: 'none', background: 'none', border: `1px solid ${c.hairline}`,
    borderRadius: 3, padding: '8px 12px', fontSize: 16, cursor: 'pointer', color: c.ink,
  },
  h1: { margin: 0, fontFamily: font.serif, fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: 500, color: c.ink },
  content: { flex: 1, padding: '26px 36px 32px', minWidth: 0 },

  card: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    padding: '26px 28px', display: 'flex', flexDirection: 'column', gap: 18,
    minWidth: 0,
  },
  cardHeadRow: {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    gap: 10, flexWrap: 'wrap',
  },
  eyebrow: {
    fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.muted,
  },
  eyebrowOnTeal: {
    fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: c.onTealDim,
  },
  small: { fontSize: 13, color: c.muted, lineHeight: 1.5 },

  overviewTop: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 22,
  },
  potBig: {
    fontFamily: font.serif, fontSize: 'clamp(38px, 5vw, 62px)', lineHeight: 0.95, color: c.ink,
    fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word',
  },
  potChange: { fontSize: 14, fontWeight: 600, paddingBottom: 8 },

  prizeGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 14,
    fontVariantNumeric: 'tabular-nums',
  },
  prizeCell: { display: 'flex', flexDirection: 'column', gap: 3 },
  prizeLabel: { fontSize: 12, color: c.muted },
  prizeValue: { fontSize: 'clamp(17px, 1.8vw, 20px)', color: c.ink, fontWeight: 600 },

  drawCard: {
    background: c.teal, borderRadius: 3, padding: '22px 24px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  drawWhen: { fontFamily: font.serif, fontSize: 'clamp(24px, 3vw, 30px)', color: c.onTeal, lineHeight: 1.1 },
  drawSub: { fontSize: 14, color: '#cfdcd6', lineHeight: 1.5 },

  countPill: {
    padding: '3px 9px', borderRadius: 11, background: c.lapsedBg,
    color: c.lapsedFg, fontSize: 13, fontWeight: 600,
  },
  nudgeRow: { display: 'flex', justifyContent: 'space-between', gap: 12 },
  linkBtn: {
    marginTop: 'auto', alignSelf: 'flex-start', background: 'none', border: 'none',
    padding: 0, fontSize: 14, fontWeight: 600, color: c.teal, cursor: 'pointer',
    borderBottom: `1px solid ${c.hairDeep}`, fontFamily: font.sans,
  },

  statStrip: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 1,
    background: c.hairline, border: `1px solid ${c.hairline}`, borderRadius: 3,
    fontVariantNumeric: 'tabular-nums', overflow: 'hidden',
  },
  statCell: {
    background: c.card, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 5,
  },
  statLabel: { fontSize: 12, color: c.muted },
  statValue: { fontFamily: font.serif, fontSize: 'clamp(20px, 2.4vw, 26px)', color: c.ink },

  barCol: {
    flex: 1, height: '100%', display: 'flex', flexDirection: 'column',
    justifyContent: 'flex-end', alignItems: 'center', gap: 7,
  },
};
